import { Button } from "@do/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@do/ui/components/tooltip";
import { cn } from "@do/ui/lib/utils";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import { useChat } from "../../../../providers/chat-provider";

interface MessageActionCopyButtonProps {
  text: string;
  id: string;
}

function fallbackCopyText(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;

  // Avoid scrolling/jumping in the page/iframe.
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  textarea.style.padding = "0";
  textarea.style.border = "0";
  textarea.style.outline = "0";
  textarea.style.boxShadow = "none";
  textarea.style.background = "transparent";
  textarea.style.opacity = "0";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  const ok = document.execCommand?.("copy");
  document.body.removeChild(textarea);

  if (!ok) throw new Error("execCommand(copy) failed");
}

async function copyText(text: string) {
  // In iframes, `navigator.clipboard.writeText` can be blocked by Permissions Policy
  // unless the host iframe allows it (e.g. <iframe allow="clipboard-write">).
  // We still try it first, then fall back to execCommand.
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // fall through to fallback
  }

  fallbackCopyText(text);
}

export const MessageActionCopyButton = ({ text, id }: MessageActionCopyButtonProps) => {
  const t = useTranslations("do-chat-v2");
  const { disabled } = useChat();

  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(() => {
    copyText(text)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(() => setIsCopied(false));
  }, [text]);

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return (
    <Tooltip group>
      <TooltipTrigger asChild>
        <Button
          id={`message-${id}-copy-button`}
          onClick={handleCopy}
          disabled={disabled}
          size="icon"
          className={cn(
            "size-fit",
            "rounded-(--message-actions-border-radius)",
            "p-(--message-actions-padding)",
            "bg-(--message-actions-background)/(--message-actions-background-opacity)",
            "hover:bg-(--message-actions-hover-background)/(--message-actions-hover-background-opacity)",
            "text-(--message-actions-foreground)/(--message-actions-foreground-opacity)",
            "hover:text-(--message-actions-hover-foreground)/(--message-actions-hover-foreground-opacity)"
          )}
          aria-label={
            isCopied
              ? t("chat.messages.accessibility.message-copied-aria-label")
              : t("chat.messages.accessibility.message-copy-aria-label")
          }
        >
          <Icon
            id={`message-${id}-copy-button-icon`}
            aria-hidden="true"
            className="size-(--message-actions-icon-size)"
          />
        </Button>
      </TooltipTrigger>

      <TooltipContent
        id={`message-${id}-copy-button-tooltip`}
        side="bottom"
        arrowClassName={cn(
          "bg-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)",
          "fill-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)"
        )}
        className={cn(
          "bg-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)",
          "text-(--message-actions-tooltip-foreground)/(--message-actions-tooltip-foreground-opacity)"
        )}
      >
        {isCopied ? t("chat.messages.actions.copied") : t("chat.messages.actions.copy")}
      </TooltipContent>
    </Tooltip>
  );
};
